import type { Ricerca } from './api/anagrafiche.js';
import { RICERCA_INIZIALE } from './api/anagrafiche.js';

/**
 * La ricerca vive nell'indirizzo del browser.
 *
 * Serve a una cosa sola, ma che nel v2 non si può fare: mandare un link a un
 * collega. «Guarda questi» invece di «apri i clienti, premi RICERCA, metti
 * Stati Uniti nella nazione e senza codice BC». In più il tasto indietro
 * funziona, e ricaricando la pagina non si perde quello che si stava
 * guardando.
 *
 * I filtri restano parametri normali e leggibili — `?citta=scafati` — non un
 * blocco codificato: un indirizzo si deve poter leggere e correggere a mano.
 */

/** I parametri che non sono filtri: valgono per ogni elenco. */
const GENERALI = ['page', 'size', 'sort', 'dir', 'q', 'archived'] as const;

export type RicercaUrl = Record<string, string | number | boolean | undefined>;

export function daUrl(search: RicercaUrl, iniziale: Ricerca = RICERCA_INIZIALE): Ricerca {
  const filters: Record<string, string | number | undefined> = {};
  for (const [chiave, valore] of Object.entries(search)) {
    if ((GENERALI as readonly string[]).includes(chiave)) continue;
    if (valore === undefined || valore === '') continue;
    filters[chiave] = valore as string | number;
  }

  const numero = (valore: unknown, ripiego: number) => {
    const n = Number(valore);
    return Number.isFinite(n) && n > 0 ? n : ripiego;
  };

  return {
    page: numero(search.page, iniziale.page),
    size: numero(search.size, iniziale.size),
    sort: typeof search.sort === 'string' ? search.sort : iniziale.sort,
    // senza `dir` nell'indirizzo vale il verso di partenza dell'elenco: gli
    // ordini nascono dal più recente, le anagrafiche dalla A
    dir: search.dir === 'desc' ? 'desc' : search.dir === 'asc' ? 'asc' : iniziale.dir,
    // il router la lascia testo (`leggiIndirizzo`), ma una vista salvata o un
    // indirizzo costruito a mano possono portarla come numero: è un PO lo stesso
    q: typeof search.q === 'string' ? search.q : typeof search.q === 'number' ? String(search.q) : '',
    archived: search.archived === true || search.archived === 'true',
    filters,
  };
}

/**
 * Nell'indirizzo finisce solo ciò che si discosta dai valori di partenza:
 * `/clienti` resta `/clienti`, e un link condiviso contiene giusto quello che
 * serve a ricreare la stessa schermata.
 */
export function aUrl(r: Ricerca, iniziale: Ricerca = RICERCA_INIZIALE): RicercaUrl {
  const search: RicercaUrl = {};
  if (r.page !== iniziale.page) search.page = r.page;
  if (r.size !== iniziale.size) search.size = r.size;
  if (r.sort !== iniziale.sort) search.sort = r.sort;
  if (r.dir !== iniziale.dir) search.dir = r.dir;
  if (r.q) search.q = r.q;
  if (r.archived) search.archived = true;
  for (const [chiave, valore] of Object.entries(r.filters)) {
    if (valore === undefined || valore === '') continue;
    // I valori arrivano dai campi come stringhe. Se non li riportiamo a
    // numero, l'indirizzo esce con le virgolette — `?countryId="220"` — perché
    // il router serializza le stringhe in JSON, e il servizio lo rifiuta. Un
    // indirizzo deve restare leggibile e ricopiabile a mano.
    // Solo se il numero si riscrive uguale: «000123» o «0039081» sono testo
    // (un PO, un telefono), e da numero perdevano gli zeri davanti; un PO di
    // venti cifre perdeva le ultime
    const numero = Number(valore);
    search[chiave] =
      typeof valore === 'string' && Number.isSafeInteger(numero) && String(numero) === valore.trim()
        ? numero
        : valore;
  }
  return search;
}

/** Validatore per le rotte: tiene solo i parametri con un valore. */
export function validaRicerca(search: Record<string, unknown>): RicercaUrl {
  const pulita: RicercaUrl = {};
  for (const [chiave, valore] of Object.entries(search)) {
    if (valore === undefined || valore === null || valore === '') continue;
    if (typeof valore === 'string' || typeof valore === 'number' || typeof valore === 'boolean') {
      pulita[chiave] = valore;
    }
  }
  return pulita;
}
